'use client'

import { Box, Flex, Input, Loader, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import {
  type CoinItem,
  CoinListType,
  useDifferenceTypeCoinList,
  useSearchCoins,
} from '@/app/[locale]/price/[slug]/_components/Trade/PriceConvertor/CoinListModal/hooks'
import styles from '@/app/[locale]/price/[slug]/_components/Trade/PriceConvertor/CoinListModal/index.module.css'
import Close from '@/assets/icons/cross-circle-fill.svg'
import Exclamation from '@/assets/icons/exclamation.svg'
import Search from '@/assets/icons/search.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'
import Sparkline from '@/components/chart/Sparkline'
import CoinIcon from '@/components/CoinIcon'
import { useI18nRouter } from '@/i18n/navigation'
import { coinPricesByIdQueryOptions } from '@/services/coin/prices'
import clsx from '@/utils/clsx'
import { currencyFormatter, toFixed } from '@/utils/number'

interface TokenSelectModalProps {
  visible: boolean
  onClose: () => void
  currentCurrency: string
  basePath?: string
  selectTitle?: string
  searchPlaceholder?: string
}

const CONTENT_HEIGHT = { base: 'calc(100dvh - 200px)', md: 500 }

function useCoinListTypes() {
  const t = useTranslations('common.coinCategoryTypes')
  return useMemo(
    () => [
      { type: CoinListType.Trending, label: t('trending') },
      { type: CoinListType.TopGainers, label: t('topGainers') },
      { type: CoinListType.TopLosers, label: t('topLosers') },
      { type: CoinListType.RecentlyAdded, label: t('recentlyAdded') },
      { type: CoinListType.TokensToWatch, label: t('tokensToWatch') },
      { type: CoinListType.MostPopular, label: t('mostPopular') },
    ],
    [t],
  )
}

function TokenRow({
  coin,
  currency,
  basePath = '',
}: {
  coin: CoinItem
  currency: string
  basePath?: string
}) {
  const router = useI18nRouter()
  const displayChangeValue = Number(toFixed((coin.percentChange ?? 0) * 100))
  const percentChangeColor =
    displayChangeValue > 0
      ? 'var(--common-success)'
      : displayChangeValue < 0
        ? 'var(--common-danger)'
        : 'var(--content-secondary)'
  const percentChangeText =
    coin.percentChange === null
      ? ''
      : (displayChangeValue > 0 ? '+' : '') + displayChangeValue + '%'

  const { symbol, value } = currencyFormatter('', coin.usdPrice)

  const [inViewport, setInViewport] = useState(false)
  const { data } = useQuery({
    ...coinPricesByIdQueryOptions({ interval: 'd', coinId: coin.id }),
    enabled: inViewport,
  })
  const prices = data?.prices?.map(price => price[1]) || []

  const onClickRow = () => {
    router.push(`${basePath}/${coin.slug}/${currency}`)
  }

  return (
    <UnstyledButton onClick={onClickRow} style={{ width: '100%' }}>
      <Flex className={styles.coinRow}>
        <CoinIcon coin={coin} />
        <Stack gap={2} ml={8} flex={3}>
          <Text variant={'body'}>{coin.name}</Text>
          <Text variant={'subtitle'} c={'var(--content-secondary)'}>
            {coin.symbol}
          </Text>
        </Stack>

        <Sparkline
          w={66}
          h={32}
          data={prices}
          inViewOptions={{ onInView: () => !inViewport && setInViewport(true) }}
          style={{ pointerEvents: 'none' }}
          c={percentChangeColor}
        />

        <Stack gap={2} flex={2} align={'flex-end'}>
          <Text variant={'body'}>{`${symbol}${value}`}</Text>
          <Text variant={'subtitle'} c={percentChangeColor}>
            {percentChangeText}
          </Text>
        </Stack>
      </Flex>
    </UnstyledButton>
  )
}

function TypeItem({
  isActive,
  label,
  onClick,
}: {
  isActive: boolean
  label: string
  onClick: () => void
}) {
  return (
    <Box
      className={clsx({
        [styles.typeItem]: true,
        [styles.typeItemActive]: isActive,
      })}
      onClick={onClick}
    >
      <Text variant={'subtitle'}>{label}</Text>
    </Box>
  )
}

export default function TokenSelectModal({
  visible,
  onClose,
  currentCurrency,
  basePath,
  selectTitle,
  searchPlaceholder,
}: TokenSelectModalProps) {
  const [activeCoinListType, setActiveCoinListType] = useState(CoinListType.Trending)
  const { displayCoinRes } = useDifferenceTypeCoinList(activeCoinListType)
  const { setKeyword, keyword, coinList, isSearching } = useSearchCoins()
  const t = useTranslations('coinListModal')
  const coinListTypes = useCoinListTypes()

  const content = (() => {
    const displayLoading = keyword ? isSearching : displayCoinRes.isLoading
    if (displayLoading)
      return (
        <Flex h={CONTENT_HEIGHT} align={'center'} justify={'center'}>
          <Loader />
        </Flex>
      )

    const coins = keyword ? coinList : displayCoinRes.coins
    if (coins.length === 0) {
      return (
        <Flex h={CONTENT_HEIGHT} direction={'column'} align={'center'} justify={'center'}>
          <Exclamation width={80} height={80} />
          <Text mt={12} variant={'body-emphasis'}>
            {t('noResultsFound')}
          </Text>
        </Flex>
      )
    }
    return (
      <ScrollArea h={CONTENT_HEIGHT} scrollbars={'y'} type={'scroll'}>
        {coins.map(coin => (
          <TokenRow key={coin.slug} coin={coin} currency={currentCurrency} basePath={basePath} />
        ))}
      </ScrollArea>
    )
  })()

  return (
    <ResponsiveModal
      title={<Text variant={'heading2'}>{selectTitle ?? t('selectACoin')}</Text>}
      opened={visible}
      onClose={onClose}
    >
      <Box>
        <Input
          classNames={{ input: styles.searchInput }}
          placeholder={searchPlaceholder ?? t('searchCoin')}
          leftSection={
            <Box mx={12}>
              <Search width={20} height={20} fill={'var(--content-secondary)'} />
            </Box>
          }
          rightSectionPointerEvents="all"
          rightSection={
            keyword ? <Close className={styles.clearIcon} onClick={() => setKeyword('')} /> : null
          }
          value={keyword}
          onChange={setKeyword}
        />
        {!keyword ? (
          <ScrollArea w={'100%'} scrollbars={'x'} mt={16} type={'scroll'}>
            <Flex direction={'row'}>
              {coinListTypes.map(item => (
                <TypeItem
                  key={item.type}
                  label={item.label}
                  isActive={item.type === activeCoinListType}
                  onClick={() => setActiveCoinListType(item.type)}
                />
              ))}
            </Flex>
          </ScrollArea>
        ) : null}

        <Stack mt={12} key={activeCoinListType}>
          {content}
        </Stack>
      </Box>
    </ResponsiveModal>
  )
}
